/**
 * Workflow Automation Redux Slice
 * 
 * Manages state for workflow automation, status transitions, readiness gates,
 * allocation validation, and 8-phase planning process.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../utils/api';

// Types
export interface ReadinessGateResult {
  quarter: number;
  totalFeatures: number;
  shovelReadyFeatures: number;
  readinessPercentage: number;
  isCompliant: boolean;
  threshold: number;
}

export interface AllocationViolation {
  entityType: string;
  entityId: string;
  entityName: string;
  requestedCapacity: number;
  allocatedCapacity: number;
  overAllocationPercentage: number;
}

export interface CategoryViolation {
  category: string;
  currentPercentage: number;
  targetPercentage: number;
  deviation: number;
}

export interface AllocationValidationResult {
  isValid: boolean;
  overAllocations: AllocationViolation[];
  categoryViolations: CategoryViolation[];
}

export interface PlanningPhaseStatus {
  phase: number;
  name: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'BLOCKED';
  completionPercentage: number;
  blockers: string[];
  nextActions: string[];
}

export interface WorkflowValidationResult {
  readinessGates: ReadinessGateResult[];
  allocations: AllocationValidationResult;
  planningPhases?: PlanningPhaseStatus[];
  healthScore: {
    overall: number;
    readiness: number;
    allocation: number;
    planning: number;
  };
  summary: {
    readinessGatesPass: number;
    readinessGatesFail: number;
    allocationValid: boolean;
    overAllocations: number;
    categoryViolations: number;
    planningPhasesCompleted: number;
    planningPhasesBlocked: number;
  };
}

export interface CategoryCompliance {
  isCompliant: boolean;
  totalCapacity: number;
  tolerance: number;
  categories: {
    [key: string]: {
      current: number;
      target: number;
      deviation: number;
      capacity: number;
      status: 'COMPLIANT' | 'VIOLATION';
    };
  };
  violations: CategoryViolation[];
}

export interface WorkflowState {
  // Validation results
  validationResult: WorkflowValidationResult | null;
  readinessGates: ReadinessGateResult[];
  allocations: AllocationValidationResult | null;
  planningPhases: PlanningPhaseStatus[];
  categoryCompliance: CategoryCompliance | null;
  
  // Loading states
  loading: {
    validation: boolean;
    readinessGates: boolean;
    allocations: boolean;
    planningPhases: boolean;
    categoryCompliance: boolean;
  };
  
  // Error states
  error: {
    validation: string | null;
    readinessGates: string | null;
    allocations: string | null;
    planningPhases: string | null;
    categoryCompliance: string | null;
  };
  
  // Filters
  filters: {
    planningPeriodId?: string;
    scenarioId?: string;
  };
}

const initialState: WorkflowState = {
  validationResult: null,
  readinessGates: [],
  allocations: null,
  planningPhases: [],
  categoryCompliance: null,
  loading: {
    validation: false,
    readinessGates: false,
    allocations: false,
    planningPhases: false,
    categoryCompliance: false,
  },
  error: {
    validation: null,
    readinessGates: null,
    allocations: null,
    planningPhases: null,
    categoryCompliance: null,
  },
  filters: {},
};

// Async thunks
export const runWorkflowValidation = createAsyncThunk(
  'workflow/runValidation',
  async (params: { planningPeriodId?: string; scenarioId?: string }) => {
    const response = await api.post('/workflow/run-validation', params);
    return response.data.data;
  }
);

export const fetchReadinessGates = createAsyncThunk(
  'workflow/fetchReadinessGates',
  async (scenarioId?: string) => {
    const params = scenarioId ? { scenarioId } : {};
    const response = await api.get('/workflow/readiness-gates', { params });
    return response.data.data;
  }
);

export const fetchReadinessGate = createAsyncThunk(
  'workflow/fetchReadinessGate',
  async (params: { quarter: number; scenarioId?: string }) => {
    const queryParams = params.scenarioId ? { scenarioId: params.scenarioId } : {};
    const response = await api.get(`/workflow/readiness-gate/${params.quarter}`, { params: queryParams });
    return response.data.data;
  }
);

export const validateAllocations = createAsyncThunk(
  'workflow/validateAllocations',
  async (scenarioId?: string) => {
    const params = scenarioId ? { scenarioId } : {};
    const response = await api.get('/workflow/validate-allocations', { params });
    return response.data.data;
  }
);

export const fetchPlanningPhases = createAsyncThunk(
  'workflow/fetchPlanningPhases',
  async (planningPeriodId: string) => {
    const response = await api.get(`/workflow/planning-phases/${planningPeriodId}`);
    return response.data.data;
  }
);

export const fetchCategoryCompliance = createAsyncThunk(
  'workflow/fetchCategoryCompliance',
  async (scenarioId?: string) => {
    const params = scenarioId ? { scenarioId } : {};
    const response = await api.get('/workflow/category-compliance', { params });
    return response.data.data;
  }
);

export const validateEpicTransition = createAsyncThunk(
  'workflow/validateEpicTransition',
  async (params: { epicId: string; currentStatus: string; targetStatus: string }) => {
    const response = await api.post(`/workflow/validate-epic-transition/${params.epicId}`, {
      currentStatus: params.currentStatus,
      targetStatus: params.targetStatus,
    });
    return response.data.data;
  }
);

export const triggerNotifications = createAsyncThunk(
  'workflow/triggerNotifications',
  async (scenarioId?: string) => {
    const response = await api.post('/workflow/trigger-notifications', { scenarioId });
    return response.data.data;
  }
);

// Slice
const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<WorkflowState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearValidationResult: (state) => {
      state.validationResult = null;
    },
    clearErrors: (state) => {
      state.error = {
        validation: null,
        readinessGates: null,
        allocations: null,
        planningPhases: null,
        categoryCompliance: null,
      };
    },
  },
  extraReducers: (builder) => {
    // Run workflow validation
    builder
      .addCase(runWorkflowValidation.pending, (state) => {
        state.loading.validation = true;
        state.error.validation = null;
      })
      .addCase(runWorkflowValidation.fulfilled, (state, action) => {
        state.loading.validation = false;
        state.validationResult = action.payload;
        state.readinessGates = action.payload.readinessGates;
        state.allocations = action.payload.allocations;
        if (action.payload.planningPhases) {
          state.planningPhases = action.payload.planningPhases;
        }
      })
      .addCase(runWorkflowValidation.rejected, (state, action) => {
        state.loading.validation = false;
        state.error.validation = action.error.message || 'Failed to run workflow validation';
      });

    // Fetch readiness gates
    builder
      .addCase(fetchReadinessGates.pending, (state) => {
        state.loading.readinessGates = true;
        state.error.readinessGates = null;
      })
      .addCase(fetchReadinessGates.fulfilled, (state, action) => {
        state.loading.readinessGates = false;
        state.readinessGates = action.payload.readinessGates;
      })
      .addCase(fetchReadinessGates.rejected, (state, action) => {
        state.loading.readinessGates = false;
        state.error.readinessGates = action.error.message || 'Failed to fetch readiness gates';
      });

    // Validate allocations
    builder
      .addCase(validateAllocations.pending, (state) => {
        state.loading.allocations = true;
        state.error.allocations = null;
      })
      .addCase(validateAllocations.fulfilled, (state, action) => {
        state.loading.allocations = false;
        state.allocations = action.payload.validation;
      })
      .addCase(validateAllocations.rejected, (state, action) => {
        state.loading.allocations = false;
        state.error.allocations = action.error.message || 'Failed to validate allocations';
      });

    // Fetch planning phases
    builder
      .addCase(fetchPlanningPhases.pending, (state) => {
        state.loading.planningPhases = true;
        state.error.planningPhases = null;
      })
      .addCase(fetchPlanningPhases.fulfilled, (state, action) => {
        state.loading.planningPhases = false;
        state.planningPhases = action.payload.phases;
      })
      .addCase(fetchPlanningPhases.rejected, (state, action) => {
        state.loading.planningPhases = false;
        state.error.planningPhases = action.error.message || 'Failed to fetch planning phases';
      });

    // Fetch category compliance
    builder
      .addCase(fetchCategoryCompliance.pending, (state) => {
        state.loading.categoryCompliance = true;
        state.error.categoryCompliance = null;
      })
      .addCase(fetchCategoryCompliance.fulfilled, (state, action) => {
        state.loading.categoryCompliance = false;
        state.categoryCompliance = action.payload;
      })
      .addCase(fetchCategoryCompliance.rejected, (state, action) => {
        state.loading.categoryCompliance = false;
        state.error.categoryCompliance = action.error.message || 'Failed to fetch category compliance';
      });
  },
});

export const { setFilters, clearValidationResult, clearErrors } = workflowSlice.actions;
export default workflowSlice.reducer;